// Fixed-budget GPU slab allocator for streamed-SOG splats — Phase 1 of the
// streaming plan (see /Users/uldis.liepins/.claude/plans/witty-watching-star.md).
//
// Unlike Splat.ts's setupSharedTextures (sized once to a model's full
// numVertices and uploaded once), this pool is created ONCE at a fixed
// `budget` splat capacity and stays that size for the life of the scene.
// Callers (SplatOctree) alloc/free contiguous ranges ("slots") as octree
// nodes change LOD, and write GPU-ready per-splat data into whatever slot
// they were given. A freed slot's stale texture data is never drawn because
// the sort worker excludes it from the index buffer (see the streaming sort
// worker added in Splat.ts) — the pool itself never needs to zero it.
//
// This module is purely additive: it does not touch, and is not touched by,
// the existing SplatLoader.load() one-shot path in Splat.ts.

import * as THREE from 'three';
import { isWebGPU, maxTextureSize, fitShDegree, setUintTextureFormat, writeTextureRowWebGPU, zeroRangeWebGPU, initPoolTexturesWebGPU, BYTES_PER_TEXEL } from './poolTextureWrite';

// Buddy allocator. Every allocation is a power-of-two block >= MIN_BUCKET.
// The difference from a plain segregated free-list (what this used to be):
// alloc() SPLITS a larger free block when the exact size isn't available,
// and free() MERGES a block with its buddy when both are free. That keeps
// free space coalesced, so a request nearly always finds a block instead of
// stranding freed space in a size class nothing wants — which is what used
// to pin the high-water mark at capacity and force a ~1s _compact() repack.
// There is no repack path now: a rare alloc failure just means a leaf holds
// its current LOD (SplatOctree handles that), never a stall.
const MIN_BUCKET = 512;
const MIN_ORDER = Math.round(Math.log2(MIN_BUCKET)); // 9

// Frames a freed block waits before it re-enters the free structure — see
// the _quarantine comment. Long enough to outlast one sort round-trip
// (worker message + sort + result message) even on a busy main thread /
// low frame rate. ~10 frames ≈ 160ms; the only cost is reclaimed space
// becoming allocatable a few frames later.
const QUARANTINE_TICKS = 10;
// With the CPU sort worker (WebGL), a frame count isn't enough: the draw
// order on screen when a block is freed stays there until a sort REQUESTED
// AFTER the free has landed — up to two sort round-trips. At 8M splats a
// sort takes ~100+ ms, so 10 frames ran out first and the block was refilled
// with another chunk while the old order still drew from it: a patch of
// misplaced splats for a frame or two while moving (2026-10-30). Splat.ts
// counts sorts posted / landed on the pool (sortsPosted, sortsLanded); once
// it has posted one, a block also waits for that landing. Safety valve: a
// block is released anyway after QUARANTINE_MAX_TICKS (no sorts landing at
// all — e.g. the mesh isn't rendered).
const QUARANTINE_MAX_TICKS = 600;

/** Shared by both pools: may a quarantined entry re-enter the free list? */
export function quarantineDone(pool, q) {
  if (q.ticksLeft > 0) return false;
  if (!pool.sortsPosted || pool.sortsLanded >= q.needSort) return true;
  return q.ticksLeft <= QUARANTINE_TICKS - QUARANTINE_MAX_TICKS;
}

function orderFor(count) {
  let o = MIN_ORDER;
  while (2 ** o < count) o++;
  return o;
}

// Exported so SplatOctree's budget solver counts TRUE pool consumption (the
// rounded-up block size), not the raw splat count.
export function nextBucket(count) {
  return 2 ** orderFor(count);
}

export class SplatPool {
  /**
   * @param {THREE.WebGLRenderer} gl
   * @param {number} budget - fixed splat capacity for the life of this pool
   * @param {number} shDegree - 0 disables the SH texture entirely
   */
  constructor(gl, budget, shDegree = 0) {
    this.gl = gl;
    this.budget = budget;
    this.shDegree = shDegree;

    this.isWebGPU = isWebGPU(gl);
    // Bumped on every write/free — WebGPU's GPU sort re-sorts when it changes.
    this.version = 0;
    this.width = Math.min(maxTextureSize(gl), budget);
    this.height = Math.floor((budget - 1) / this.width) + 1;
    // Actual addressable capacity is width*height (rounded up from budget to
    // a full last row) — allocation still refuses anything past `budget`
    // itself so callers can't rely on the rounding slack.
    this.capacity = this.width * this.height;

    this.centerAndScaleData = new Float32Array(this.width * this.height * 4);
    this.covAndColorData = new Uint32Array(this.width * this.height * 4);
    this.centerAndScaleTexture = new THREE.DataTexture(
      this.centerAndScaleData, this.width, this.height, THREE.RGBAFormat, THREE.FloatType
    );
    this.centerAndScaleTexture.needsUpdate = true;

    this.covAndColorTexture = new THREE.DataTexture(
      this.covAndColorData, this.width, this.height, THREE.RGBAIntegerFormat, THREE.UnsignedIntType
    );
    setUintTextureFormat(gl, this.covAndColorTexture);
    this.covAndColorTexture.needsUpdate = true;

    // SH texture height = height × rows per splat (12 for degree 3). Lower
    // the degree until it fits the GPU's max texture size (WebGPU's default
    // limit is 8192 — an 8M budget at degree 3 needs 11,724) and memory cap
    // (see fitShDegree) rather than creating an invalid texture; 0 = no SH.
    shDegree = fitShDegree(this.width, this.height, shDegree, maxTextureSize(gl));
    this.shDegree = shDegree;
    this.hasSH = shDegree > 0;
    this.shRowsPerSplat = shDegree === 1 ? 3 : shDegree === 2 ? 9 : shDegree >= 3 ? 12 : 0;
    if (this.hasSH) {
      const shHeight = this.height * this.shRowsPerSplat;
      this.shData = new Uint16Array(this.width * shHeight * 4);
      this.shTexture = new THREE.DataTexture(
        this.shData, this.width, shHeight, THREE.RGBAFormat, THREE.HalfFloatType
      );
      this.shTexture.needsUpdate = true;
    } else {
      this.shData = null;
      this.shTexture = null;
    }

    // Buddy allocator state.
    //  _free[order]  = Set of free block offsets at that order (block size
    //                  2**order). A Set (not an array) so buddy-merge can do
    //                  an O(1) "is my buddy free?" check.
    //  _liveOrder    = Map offset -> order for every currently-allocated
    //                  block, so free() knows the size without the caller
    //                  passing it back.
    this._maxOrder = Math.max(MIN_ORDER, Math.ceil(Math.log2(this.capacity)));
    this._free = [];
    for (let o = 0; o <= this._maxOrder; o++) this._free.push(new Set());
    this._liveOrder = new Map();
    // Seed the free structure with [0, capacity) decomposed into the largest
    // buddy-aligned power-of-two blocks. (capacity is width*height, not
    // necessarily a power of two, so this is a handful of blocks, not one.)
    let s = 0;
    while (s < this.capacity) {
      let o = this._maxOrder;
      while (o > MIN_ORDER && (s % (2 ** o) !== 0 || s + 2 ** o > this.capacity)) o--;
      if (s % (2 ** o) !== 0 || s + 2 ** o > this.capacity) break; // tail smaller than MIN_BUCKET — unusable, ignore
      this._free[o].add(s);
      s += 2 ** o;
    }
    this.residentCount = 0; // sum of live raw (un-rounded) counts

    // Freed blocks wait here for a few tick()s before re-entering _free.
    // The sort worker's "this slot is live" state and the GPU texture at
    // that slot update over separate async channels; handing a just-freed
    // block to a new chunk immediately can let an in-flight sort list the
    // OLD chunk's index there while the texture already holds the NEW data —
    // a block of misplaced splats for a frame or two. Quarantine covers that
    // window.
    this._quarantine = []; // { offset, order, ticksLeft, needSort }
    this.sortsPosted = 0; // see quarantineDone
    this.sortsLanded = 0;
  }

  /** Reserves a power-of-two block big enough for `count` slots. Returns the
   * block offset, or -1 if no block is available (SplatOctree then keeps the
   * leaf at its current LOD — there is no repack fallback). */
  /** See SplatPoolExact.usedTop — the buddy allocator doesn't track it. */
  get usedTop() { return this.capacity; }

  alloc(count) {
    if (count <= 0) return -1;
    const want = orderFor(count);
    // Find the smallest free block of order >= want.
    let o = want;
    while (o <= this._maxOrder && this._free[o].size === 0) o++;
    if (o > this._maxOrder) return -1;
    // Take it and split down to `want`, returning each unused upper half.
    let offset = this._free[o].values().next().value;
    this._free[o].delete(offset);
    while (o > want) {
      o--;
      const buddy = offset + 2 ** o;
      this._free[o].add(buddy);
    }
    this._liveOrder.set(offset, want);
    this.residentCount += count;
    return offset;
  }

  /** Advance the freed-block quarantine by one frame. Call once per frame
   * (SplatOctree.update). Blocks whose quarantine elapsed re-enter the free
   * structure (merging with a free buddy where possible). */
  tick() {
    if (this._quarantine.length === 0) return;
    const keep = [];
    for (const q of this._quarantine) {
      q.ticksLeft--;
      if (!quarantineDone(this, q)) { keep.push(q); continue; }
      this._release(q.offset, q.order);
    }
    this._quarantine = keep;
  }

  _release(offset, order) {
    let o = order;
    // Coalesce upward while the buddy at this order is also free.
    while (o < this._maxOrder) {
      const buddy = offset ^ (2 ** o);
      if (!this._free[o].has(buddy)) break;
      this._free[o].delete(buddy);
      offset = Math.min(offset, buddy);
      o++;
    }
    this._free[o].add(offset);
  }

  /** Returns a previously-alloc'd block to the pool (after quarantine).
   * `count` is ignored for sizing — the order is looked up from alloc(). */
  free(offset, count) {
    const order = this._liveOrder.get(offset);
    if (order === undefined) return; // double-free / not ours — ignore
    this._liveOrder.delete(offset);
    this.residentCount = Math.max(0, this.residentCount - count);
    this._zeroSlotsWebGPU(offset, 2 ** order);
    this.version++;
    this._quarantine.push({ offset, order, ticksLeft: QUARANTINE_TICKS, needSort: this.sortsPosted + 1 });
  }

  /** Invariant check — call from the console via
   * window.__splatOctree.pool._audit(). Collects every live / free /
   * quarantined block, sorts by offset, and verifies they don't overlap and
   * tile [0, capacity) with at most a sub-MIN_BUCKET tail gap. Returns
   * { ok, ... } and console.errors on any violation. Zero cost unless called. */
  _audit() {
    const blocks = [];
    for (const [off, ord] of this._liveOrder) blocks.push([off, 2 ** ord, 'live']);
    for (let o = 0; o < this._free.length; o++) for (const off of this._free[o]) blocks.push([off, 2 ** o, 'free']);
    for (const q of this._quarantine) blocks.push([q.offset, 2 ** q.order, 'quar']);
    blocks.sort((a, b) => a[0] - b[0]);
    let cursor = 0;
    const problems = [];
    for (const [off, size, kind] of blocks) {
      if (off < cursor) problems.push(`overlap: ${kind} block at ${off} (size ${size}) starts before cursor ${cursor}`);
      else if (off > cursor) problems.push(`gap: ${off - cursor} unaccounted slots at ${cursor}`);
      cursor = Math.max(cursor, off + size);
    }
    const tail = this.capacity - cursor;
    if (tail >= MIN_BUCKET) problems.push(`tail gap: ${tail} slots after ${cursor} (>= MIN_BUCKET)`);
    if (cursor > this.capacity) problems.push(`overrun: cursor ${cursor} > capacity ${this.capacity}`);
    const ok = problems.length === 0;
    if (!ok) console.error('[SplatPool._audit] FAIL', problems);
    return { ok, blocks: blocks.length, live: this._liveOrder.size, residentCount: this.residentCount, problems };
  }

  /** Cheap enough to call every solve (just scans this._free's ~20 order
   * buckets, not the full block list like _audit()) — surfaces whether the
   * pool has room but can't USE it (fragmentation: totalFree substantial,
   * largestBlock tiny) vs is genuinely full (both near zero). Added
   * 2026-09-15 chasing allocFailures in the tens of thousands while
   * residentCount was still well under capacity — see splatOctree.js's
   * ALLOC_FAIL_BACKOFF_MS comment. */
  freeStats() {
    let totalFree = 0, largestOrder = -1;
    for (let o = 0; o <= this._maxOrder; o++) {
      const n = this._free[o].size;
      if (n === 0) continue;
      totalFree += n * (2 ** o);
      largestOrder = o; // ascending loop — last non-empty is the largest
    }
    return { totalFree, largestFreeBlock: largestOrder >= 0 ? 2 ** largestOrder : 0 };
  }

  /** Writes `count` splats' worth of GPU-ready data starting at `offset`.
   * `chunk.centerAndScale` is a Float32Array(count*4), `chunk.covAndColor`
   * a Uint32Array(count*4), `chunk.sh` (if this.hasSH) a
   * Uint16Array(count*shRowsPerSplat*4) — all per-splat contiguous, i.e. the
   * same layout Splat.ts's pushSOGData already builds before uploading.
   * Generalizes the chunked xoffset/yoffset texSubImage2D loop that already
   * exists in Splat.ts's pushDataBuffer (arbitrary start offset instead of
   * always appending at loadedVertexCount) — new code here rather than
   * touching that function, since pushDataBuffer backs the untouched
   * .splat streaming path.
   */
  // Bracket a run of write() calls to pay gl.resetState() ONCE for the whole
  // batch instead of per chunk. resetState() forces three.js to re-bind its
  // entire cached GL state on the next draw; doing it per chunk while a
  // frame's worth of decodes flush was a real slice of the upload-burst
  // frame spike. Nesting is a no-op (ref-counted).
  beginWrites() { this._writeDepth = (this._writeDepth || 0) + 1; }
  endWrites() {
    this._writeDepth = Math.max(0, (this._writeDepth || 0) - 1);
    if (this._writeDepth === 0 && this._dirtyWrites && !this.isWebGPU) { this.gl.resetState(); this._dirtyWrites = false; }
  }

  write(offset, count, chunk) {
    this.version++;
    if (this.isWebGPU) {
      this._writeTextureWebGPU(this.centerAndScaleTexture, chunk.centerAndScale, offset, count);
      this._writeTextureWebGPU(this.covAndColorTexture, chunk.covAndColor, offset, count);
      if (this.hasSH && this.shTexture && chunk.sh) this._writeShTextureWebGPU(chunk.sh, offset, count);
      return;
    }
    const context = this.gl.getContext();
    this._writeTexture(context, this.centerAndScaleTexture, chunk.centerAndScale, context.RGBA, context.FLOAT, offset, count, 4);
    this._writeTexture(context, this.covAndColorTexture, chunk.covAndColor, context.RGBA_INTEGER, context.UNSIGNED_INT, offset, count, 4);
    if (this.hasSH && this.shTexture && chunk.sh) {
      this._writeShTexture(context, chunk.sh, offset, count);
    }
    this._dirtyWrites = true;
    if (!this._writeDepth) { this.gl.resetState(); this._dirtyWrites = false; } // not inside a begin/end batch
  }

  _writeTexture(context, tex, data, fmt, type, offset, count, comps) {
    const props = this.gl.properties.get(tex);
    context.bindTexture(context.TEXTURE_2D, props.__webglTexture);
    let remaining = count;
    let pos = offset;
    let srcOff = 0;
    while (remaining > 0) {
      const xoffset = pos % this.width;
      const yoffset = Math.floor(pos / this.width);
      const rowSpan = Math.min(this.width - xoffset, remaining);
      context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, rowSpan, 1, fmt, type, data, srcOff * comps);
      pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
    }
  }

  // `shData` is per-splat contiguous: splat i's `rows` texel-rows of RGBA16
  // data occupy shData[i*rows*4 .. (i+1)*rows*4) ("splat-major"). The
  // destination texture wants the opposite — for a batched texSubImage2D
  // per destination row, the source needs `rows` contiguous blocks of
  // `count` texels each ("row-major"). Re-layout once here (plain typed-
  // array copies — cheap) instead of the earlier one-texSubImage2D-per-
  // (splat,row) version, which was O(count * rows) individual GL calls —
  // e.g. ~360k calls for a single 30k-splat degree-3 chunk. That was the
  // actual reason SH ended up disabled for real testing rather than left
  // on: this brings it down to `rows` batched calls per write (12 for
  // degree 3), matching how centerAndScale/covAndColor already upload.
  _writeShTexture(context, shData, offset, count) {
    const props = this.gl.properties.get(this.shTexture);
    context.bindTexture(context.TEXTURE_2D, props.__webglTexture);
    const rows = this.shRowsPerSplat;

    const rowMajor = new Uint16Array(count * rows * 4);
    for (let i = 0; i < count; i++) {
      for (let r = 0; r < rows; r++) {
        const src = (i * rows + r) * 4;
        const dst = (r * count + i) * 4;
        rowMajor[dst] = shData[src];
        rowMajor[dst + 1] = shData[src + 1];
        rowMajor[dst + 2] = shData[src + 2];
        rowMajor[dst + 3] = shData[src + 3];
      }
    }

    for (let r = 0; r < rows; r++) {
      const rowSlice = rowMajor.subarray(r * count * 4, (r + 1) * count * 4);
      let remaining = count, pos = offset, srcOff = 0;
      while (remaining > 0) {
        const xoffset = pos % this.width;
        const yoffset = Math.floor(pos / this.width) * rows + r;
        const rowSpan = Math.min(this.width - xoffset, remaining);
        context.texSubImage2D(context.TEXTURE_2D, 0, xoffset, yoffset, rowSpan, 1, context.RGBA, context.HALF_FLOAT, rowSlice, srcOff * 4);
        pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
      }
    }
  }

  // Called once the GL textures are materialised. The CPU-side backing
  // arrays existed only to seed them (they're `width*height*4` — ~128 MB
  // each for centerAndScale/covAndColor at an 8M budget, plus SH); every
  // subsequent write goes straight to VRAM via texSubImage2D, so drop them.
  // The DataTexture's own reference is nulled too so the buffers can be GC'd
  // — the only thing that would re-read them is a full texture re-upload
  // (WebGL context loss), which tears down the whole streaming session
  // anyway.
  // WebGPU twins of _writeTexture/_writeShTexture: same row-span walk, one
  // GPUQueue.writeTexture per destination row span.
  _writeTextureWebGPU(tex, data, offset, count) {
    let remaining = count, pos = offset, srcOff = 0;
    while (remaining > 0) {
      const xoffset = pos % this.width;
      const yoffset = Math.floor(pos / this.width);
      const rowSpan = Math.min(this.width - xoffset, remaining);
      writeTextureRowWebGPU(this.gl, tex, xoffset, yoffset, rowSpan, data.subarray(srcOff * 4, (srcOff + rowSpan) * 4), BYTES_PER_TEXEL.rgba32);
      pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
    }
  }

  _writeShTextureWebGPU(shData, offset, count) {
    const rows = this.shRowsPerSplat;
    for (let r = 0; r < rows; r++) {
      // Gather row r of each splat (source is splat-major, see _writeShTexture).
      let remaining = count, pos = offset, srcOff = 0;
      while (remaining > 0) {
        const xoffset = pos % this.width;
        const yoffset = Math.floor(pos / this.width) * rows + r;
        const rowSpan = Math.min(this.width - xoffset, remaining);
        const out = new Uint16Array(rowSpan * 4);
        for (let i = 0; i < rowSpan; i++) {
          const src = ((srcOff + i) * rows + r) * 4;
          out[i * 4] = shData[src]; out[i * 4 + 1] = shData[src + 1];
          out[i * 4 + 2] = shData[src + 2]; out[i * 4 + 3] = shData[src + 3];
        }
        writeTextureRowWebGPU(this.gl, this.shTexture, xoffset, yoffset, rowSpan, out, BYTES_PER_TEXEL.rgba16);
        pos += rowSpan; remaining -= rowSpan; srcOff += rowSpan;
      }
    }
  }

  // WebGPU: freed slots must become invisible to the GPU sort (it has no
  // live-slot list, unlike the WebGL CPU sort worker) — zero their
  // covAndColor texels (alpha 0 → skipped).
  _zeroSlotsWebGPU(offset, count) {
    if (!this.isWebGPU) return;
    zeroRangeWebGPU(this.gl, this.covAndColorTexture, this.width, offset, count, BYTES_PER_TEXEL.rgba32);
  }

  // WebGPU: create the GPU textures now, then drop the CPU seed arrays
  // (the WebGL path does this from connect() once its textures exist).
  initGpuTextures() {
    if (!this.isWebGPU || this._gpuTexturesReady) return;
    initPoolTexturesWebGPU(this.gl, [this.centerAndScaleTexture, this.covAndColorTexture, this.shTexture]);
    this._gpuTexturesReady = true;
    this.releaseInitBuffers();
  }

  releaseInitBuffers() {
    if (this._initBuffersReleased) return;
    this._initBuffersReleased = true;
    for (const tex of [this.centerAndScaleTexture, this.covAndColorTexture, this.shTexture]) {
      if (!tex) continue;
      if (tex.image) tex.image.data = null;
      if (tex.source && tex.source.data) tex.source.data = null;
    }
    this.centerAndScaleData = null;
    this.covAndColorData = null;
    this.shData = null;
  }

  destroy() {
    this.centerAndScaleTexture.dispose();
    this.covAndColorTexture.dispose();
    this.shTexture?.dispose();
  }
}
